import { arcWindowFromHalfCircumference, isValidArcWindow, wrapMm } from './arc-window';
import type { CylinderParams } from './cylinder-params';
import { sourceToWarped, warpedToSource } from './cylinder-point';
import { arcMmToAreaPx, areaPxToArcMm } from './flat-area';
import type { Point } from './types';

/**
 * 원통 서피스의 펼친 프레임 크기(mm). 폭은 면마다 보이는 반둘레 W(프레임 모델 B), 높이는 상품 높이 H 다.
 * 계약의 서피스 실측(`faces.<면>.surfaces[].realSize`)과 같은 구조다.
 */
export interface FrameSize {
  readonly widthMm: number;
  readonly heightMm: number;
}

/**
 * 프레임 위 점(mm). 원점은 프레임 왼쪽 위이고 x 는 오른쪽, y 는 아래가 + 다. x 는 왼쪽 실루엣 끝에서 잰 호 길이다.
 * 계약의 배치 위치(`faces.<면>.placements[].position`)와 같은 구조다.
 */
export interface FramePoint {
  readonly xMm: number;
  readonly yMm: number;
}

/** 앞면 창 [0, W] 안의 가로 구간(mm). fromMm < toMm 이다. */
export interface ArcSegment {
  readonly fromMm: number;
  readonly toMm: number;
}

/** 프레임 높이가 0 보다 큰 유한수인지 본다. 폭은 가시 호 창이 거른다(0 이하·숫자 아님·둘레 넘침이면 잘못된 창). */
function hasValidHeight({ heightMm }: FrameSize): boolean {
  return Number.isFinite(heightMm) && heightMm > 0;
}

/**
 * 프레임 점(mm)을 사진 점(px)으로 보낸다.
 * - 가로: 프레임 모델 B 창(둘레 2W, 앞면 [0, W])으로 감아 area px 로 바꾼다. 감은 위치가 뒷면 (W, 2W) 이면 null 이다.
 * - 세로: area.top + yMm/H × area.height 로 area 높이에 선형으로 맞춘다.
 * - 그 area 점을 sourceToWarped 로 투영한다.
 * yMm 이 프레임 높이 밖이거나, 프레임 크기·params 가 잘못됐으면 null 이다.
 */
export function frameToWarped(
  params: CylinderParams,
  frame: FrameSize,
  point: FramePoint,
): Point | null {
  if (!hasValidHeight(frame)) return null;
  const { area } = params;
  const x = arcMmToAreaPx(point.xMm, area, arcWindowFromHalfCircumference(frame.widthMm));
  if (x === null) return null;
  return sourceToWarped({ x, y: area.top + (point.yMm / frame.heightMm) * area.height }, params);
}

/**
 * 사진 점(px)을 프레임 점(mm)으로 되돌린다. warpedToSource 로 area 점을 구한 뒤,
 * 가로는 같은 창으로 호 길이를 구하고 세로는 area 높이 비율로 구한다.
 * 뒷면 위치로 감지 않으므로 결과는 [0, W]×[0, H] 안이다. 세로 비율이 반올림으로 넘친 만큼은 [0, 1] 로 자른다.
 * 실루엣 밖이거나 프레임 크기·params 가 잘못됐으면 null 이다.
 */
export function warpedToFrame(
  params: CylinderParams,
  frame: FrameSize,
  point: Point,
): FramePoint | null {
  if (!hasValidHeight(frame)) return null;
  const source = warpedToSource(point, params);
  if (source === null) return null;
  const { area } = params;
  const xMm = areaPxToArcMm(source.x, area, arcWindowFromHalfCircumference(frame.widthMm));
  if (xMm === null) return null;
  const fraction = Math.min(1, Math.max(0, (source.y - area.top) / area.height));
  return { xMm, yMm: fraction * frame.heightMm };
}

/**
 * 배치 가로 구간 [startMm, startMm + widthMm] 을 둘레 2W 로 감아 앞면 창 [0, W] 와 겹치는 조각을
 * 시작 mm 오름차순으로 돌려준다(Phase 5 결정 Q6). frameToWarped 와 같은 감김 규칙이고 조각은 최대 둘이다.
 * - 감은 시작이 창 안이면 [시작, min(시작 + 폭, W)]
 * - 둘레를 넘어간 꼬리가 있으면 앞면 왼쪽 [0, min(꼬리 끝, W)]
 * 폭 0 조각(창 끝에 닿기만 하는 구간)은 넣지 않는다.
 * 폭이 둘레인 배치처럼 두 조각이 맞닿아도 합치지 않는다. 맞닿은 곳이 컴포넌트의 왼쪽·오른쪽 변이라 경계로 그려야 한다.
 * 폭이 둘레보다 넓으면 둘레로 본다(계약은 이런 배치를 거부한다).
 * 시작·폭이 유한수가 아니거나 폭이 0 이하이거나, 창이 잘못됐으면(프레임 폭 0 이하·숫자 아님·둘레 넘침) 빈 배열이다.
 */
export function visibleArcSegments(
  startMm: number,
  widthMm: number,
  frameWidthMm: number,
): readonly ArcSegment[] {
  const arcWindow = arcWindowFromHalfCircumference(frameWidthMm);
  if (
    !isValidArcWindow(arcWindow) ||
    !Number.isFinite(startMm) ||
    !Number.isFinite(widthMm) ||
    widthMm <= 0
  ) {
    return [];
  }
  const { circumferenceMm, lengthMm } = arcWindow;
  const span = Math.min(widthMm, circumferenceMm);
  // 둘레의 음의 배수는 −0 으로 감기므로 0 으로 맞춘다.
  const start = Math.max(0, wrapMm(startMm, circumferenceMm));
  // 꼬리 끝 = 시작 + 폭 − 둘레. (둘레 − 폭)을 먼저 빼서 폭이 둘레와 같으면 정확히 시작이 되게 한다.
  const tail = { fromMm: 0, toMm: Math.min(start - (circumferenceMm - span), lengthMm) };
  const head = { fromMm: start, toMm: Math.min(start + span, lengthMm) };
  return [tail, head].filter(({ fromMm, toMm }) => toMm > fromMm);
}
